import { useEffect, useMemo, useRef, useState } from 'react';
import { get } from '@vigilo/api-client';
import type { OperationAlertSummary } from './useOperationAlertSummaries';

interface AlertListResponse {
  summary?: OperationAlertSummary;
}

export type ZoneAlertSummaryScope = 'operation' | 'global';

export interface ZoneAlertSummaryOptions {
  /** Poll interval in ms (clamped to >= 1000). Default 2000, matching useOperationAlertSummaries. */
  pollMs?: number;
  /** Change this value (e.g. the live alert-stream count) to trigger an immediate re-fetch. */
  refreshKey?: number | string;
}

export const ZONE_ALERT_SUMMARY_POLL_MS = 2000;

export interface ZoneAlertSummaryState {
  summariesByZoneId: Map<string, OperationAlertSummary>;
  loading: boolean;
  error: string | null;
}

const EMPTY_SUMMARY: OperationAlertSummary = {
  total: 0,
  active: 0,
  open: 0,
  acknowledged: 0,
  escalated: 0,
  resolved: 0,
};

function unwrapSummary(body: unknown): OperationAlertSummary {
  const direct = body as AlertListResponse;
  const envelope = body as { data?: AlertListResponse };
  return direct?.summary ?? envelope.data?.summary ?? EMPTY_SUMMARY;
}

function queryFor(operationId: string | null | undefined, zoneId: string): string {
  const params = new URLSearchParams();
  if (operationId) params.set('operationId', operationId);
  params.set('zoneIds', zoneId);
  params.set('page', '1');
  params.set('pageSize', '1');
  return params.toString();
}

export function useZoneAlertSummaries(
  operationId: string | null | undefined,
  zoneIds: readonly string[],
  enabled = true,
  scope: ZoneAlertSummaryScope = 'operation',
  options: ZoneAlertSummaryOptions = {},
): ZoneAlertSummaryState {
  const pollMs = Math.max(1000, options.pollMs ?? ZONE_ALERT_SUMMARY_POLL_MS);
  const refreshKey = options.refreshKey ?? '';
  const [summariesByZoneId, setSummariesByZoneId] = useState<Map<string, OperationAlertSummary>>(() => new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadedKeyRef = useRef<string | null>(null);
  const operationZoneKey = useMemo(
    () => `${scope}:${operationId ?? ''}:${[...new Set(zoneIds)].sort().join(',')}`,
    [operationId, scope, zoneIds],
  );

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const scopedZoneIds = [...new Set(zoneIds.map(zoneId => zoneId.trim()).filter(Boolean))];
    if (!enabled || (scope === 'operation' && !operationId) || scopedZoneIds.length === 0) {
      loadedKeyRef.current = null;
      setError(null);
      setSummariesByZoneId(prev => (prev.size === 0 ? prev : new Map()));
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // A different scope/operation/zone set must never show the previous scope's counts.
    if (loadedKeyRef.current !== operationZoneKey) {
      setSummariesByZoneId(prev => (prev.size === 0 ? prev : new Map()));
    }

    async function loadSummaries(initial: boolean) {
      if (inFlight) return;
      inFlight = true;
      if (initial) setLoading(true);
      try {
        const entries = await Promise.all(scopedZoneIds.map(async zoneId => {
          const summary = unwrapSummary(await get<unknown>(`/alerts?${queryFor(operationId, zoneId)}`));
          return [zoneId, summary] as const;
        }));
        if (!cancelled) {
          loadedKeyRef.current = operationZoneKey;
          setSummariesByZoneId(new Map(entries));
          setError(null);
        }
      } catch {
        // Keep the last good counts on a transient failure; only surface the error.
        if (!cancelled) setError('Unable to load zone alert summaries.');
      } finally {
        inFlight = false;
        if (!cancelled && initial) setLoading(false);
      }
    }

    void loadSummaries(true);
    const interval = window.setInterval(() => {
      void loadSummaries(false);
    }, pollMs);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, operationId, operationZoneKey, scope, pollMs, refreshKey]);

  return { summariesByZoneId, loading, error };
}
